import { IconButton, Tooltip } from "@mui/material";
import { ImageName } from "components/helper";
import SearchField from "components/SearchField";
import useKothar from "context/useKothar";
import { differenceInDays } from "date-fns";
import { useEffect, useState } from "react";
import { AiFillDelete, AiFillEdit, AiFillEye } from "react-icons/ai";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { IoIosRefresh } from "react-icons/io";
import { API_URL } from "const/constants";
import { toast } from "react-toastify";
import axios from "axios";
import { isSuperAdmin } from "utils/permissionUtils";
import RestoreClientModal from "./RestoreClientModal";
import DeleteModal from "components/Modals/DeleteModal";

const formatArchivedOn = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const ArchivedClients = ({ color = "light" }) => {
  const tableHeadClass = "table-head";
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [{ archivedClientList, token }, { refetchArchivedClient }] = useKothar();
  const [openConfirmationModal, setOpenConfirmationModal] = useState({
    id: null,
    state: false,
  });
  const [deleteTarget, setDeleteTarget] = useState({ id: null, state: false });
  const [deletePreview, setDeletePreview] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const isSuperAdminUser = isSuperAdmin();

  const [filteredData, setFilteredData] = useState(archivedClientList);

  useEffect(() => {
    refetchArchivedClient();
  }, []);

  useEffect(() => {
    if (searchText.length > 0) {
      const filtered = archivedClientList?.filter(
        (client) =>
          client?.name?.toLowerCase().includes(searchText?.toLowerCase()) ||
          client?.email?.toLowerCase().includes(searchText?.toLowerCase()) ||
          client?.visaStatus?.toLowerCase().includes(searchText?.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(archivedClientList);
    }
  }, [searchText, archivedClientList]);

  const restoreClient = (id) => {
    axios({
      url: `${API_URL}/clients/unarchive/${openConfirmationModal?.id}`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.data?.success === false) {
          toast.error(res.data?.message || "Error Restoring Data");
        } else {
          toast.success("Client Restored Successfully");
          setOpenConfirmationModal({ id: null, state: false });
          refetchArchivedClient();
        }
      })
      .catch(() => {
        toast.error("Error Restoring Data");
      });
  };

  const openDeletePermanentModal = (id) => {
    if (!isSuperAdminUser) {
      toast.error("Only a super admin can permanently delete a client");
      return;
    }
    setDeleteTarget({ id, state: true });
    setDeletePreviewLoading(true);
    axios
      .get(`${API_URL}/clients/${id}/delete-preview`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setDeletePreview(res.data);
      })
      .catch(() => {
        toast.error("Error loading delete preview");
      })
      .finally(() => setDeletePreviewLoading(false));
  };

  const closeDeletePermanentModal = () => {
    setDeleteTarget({ id: null, state: false });
    setDeletePreview(null);
  };

  const deleteClientPermanently = () => {
    if (!isSuperAdminUser) {
      toast.error("Only a super admin can permanently delete a client");
      return;
    }
    axios({
      url: `${API_URL}/clients/${deleteTarget?.id}/permanent`,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.data?.success === false) {
          toast.error(res.data?.message || "Error deleting client");
        } else {
          toast.success("Client permanently deleted");
          closeDeletePermanentModal();
          refetchArchivedClient();
        }
      })
      .catch(() => {
        toast.error("Error deleting client");
      });
  };

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-5">
              <div className="flex justify-start gap-4 items-center">
                <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
                <h3 className={"font-semibold text-lg " + "text-app-text"}>Archived Clients</h3>
              </div>
              <div className="flex-1 w-3/4">
                <SearchField {...{ type: "Client", searchText, setSearchText }} />
              </div>
            </div>
          </div>
          <div className="block w-full overflow-x-auto">
            <table className="items-center w-full bg-transparent border-collapse">
              <thead>
                <tr>
                  <th className={"table-head " + tableHeadClass}>S.N.</th>
                  <th className={"table-head " + tableHeadClass}>Name</th>
                  <th className={"table-head " + tableHeadClass}>Email</th>
                  <th className={"table-head " + tableHeadClass}>Phone Number</th>
                  <th className={"table-head " + tableHeadClass}>Passport Number</th>
                  <th className={"table-head " + tableHeadClass}>Visa Status</th>{" "}
                  <th className={"table-head " + tableHeadClass}>Visa Expiry</th>
                  <th className={"table-head " + tableHeadClass}>Archived By</th>
                  <th className={"table-head " + tableHeadClass}>Archived On</th>
                  <th className={"table-head " + tableHeadClass}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredData?.length > 0 ? (
                  filteredData?.map((item, index) => (
                    <tr key={item?.id || index}>
                      <td className="table-data">{index + 1}</td>
                      <td className="table-data text-left flex items-center">
                        {ImageName(item?.name)}
                        <span className="ml-3 font-bold text-app-text-secondary">
                          {item?.name || "-"}
                        </span>
                      </td>
                      <td className="table-data">{item?.email || "-"}</td>
                      <td className="table-data">
                        <div className="flex">{item?.number || "-"}</div>
                      </td>
                      <td className="table-data">
                        <div className="flex items-center gap-2">{item?.passportNumber || "-"}</div>
                      </td>
                      <td className="table-data">
                        <div className="flex items-center">{item?.visaStatus || "-"}</div>
                      </td>
                      <td className="table-data">
                        <div
                          className={
                            "flex items-center " +
                            (differenceInDays(new Date(item?.visaExpiry), new Date()) < 90
                              ? " text-red-500"
                              : "")
                          }
                        >
                          {item?.visaExpiry || "-"}
                        </div>
                      </td>
                      <td className="table-data">
                        <div className="flex items-center">{item?.updatedBy || "-"}</div>
                      </td>
                      <td className="table-data">
                        <div className="flex items-center">
                          {formatArchivedOn(item?.updatedDate)}
                        </div>
                      </td>

                      <td className="table-data text-right">
                        <div className="flex items-center">
                          <Tooltip title="Restore Client" arrow>
                            <IconButton
                              onClick={() =>
                                setOpenConfirmationModal({
                                  id: item?.id,
                                  state: true,
                                })
                              }
                            >
                              <IoIosRefresh className="text-primary cursor-pointer" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Client" arrow>
                            <IconButton
                              onClick={() =>
                                navigate(`/admin/client/edit/${item?.id}`, {
                                  state: { item },
                                })
                              }
                            >
                              <AiFillEdit className="text-primary cursor-pointer" />
                            </IconButton>
                          </Tooltip>
                          {isSuperAdminUser && (
                            <Tooltip title="Permanently Delete" arrow>
                              <IconButton onClick={() => openDeletePermanentModal(item?.id)}>
                                <AiFillDelete className="text-red-600 cursor-pointer" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr key={1}>
                    <td colSpan={10}>
                      <div className="text-lg text-center my-10">No Results Found</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {openConfirmationModal.state && (
        <RestoreClientModal
          open={openConfirmationModal.state}
          item="Client"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={() => restoreClient()}
        />
      )}
      {deleteTarget.state && (
        <DeleteModal
          open={deleteTarget.state}
          item="Client"
          preview={deletePreview}
          previewLoading={deletePreviewLoading}
          bodyText="This will permanently delete this client and everything tied to it — admissions, visa/insurance/skill-assessment/RPL records, tasks, appointments, and more. This cannot be undone."
          handleCancel={closeDeletePermanentModal}
          handleDelete={deleteClientPermanently}
        />
      )}
    </div>
  );
};

export default ArchivedClients;
